import React from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'
import { PropsWithChildrenRequired } from '../../../../types/PropsWithChildrenRequired'

type LinkButtonProps = {
    type: 'link'
    to: string
    target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target']
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

type ClickButtonProps = {
    type: 'button'
    onClick: () => void
}

type SubmitButtonProps = {
    type: 'submit'
}

type Props = {
    style: 'primary' | 'secondary' | 'danger' | 'mild-danger' | 'fab'
    size?: 'normal' | 'small'
    classes?: string
    enabled?: boolean
} & (LinkButtonProps | ClickButtonProps | SubmitButtonProps)

function Button(props: PropsWithChildrenRequired<Props>): React.JSX.Element {
    const { children, style, size = 'normal', classes, enabled = true } = props

    const baseClasses = clsx(
        'text-nowrap rounded-full font-bold transition-colors duration-300 select-none',
        'inline-flex items-center justify-center align-middle text-center',
    )

    const sizeClasses =
        size === 'normal' ? 'py-2 px-4 h-[42px]' : 'py-1 px-2 text-sm h-[30px]'

    // Classes when the button is enabled. They include hover styles.
    const enabledClasses = {
        primary: 'bg-ctxt-blue text-white hover:bg-ctxt-blue-dark',
        secondary:
            'bg-white text-gray-700 hover:bg-gray-200 border border-gray-300',
        danger: 'bg-red-500 text-white hover:bg-red-600',
        'mild-danger':
            'bg-white text-gray-700 border border-gray-300 hover:text-red-600 hover:border-red-600',
        fab: [
            'bg-ctxt-blue-light text-white',
            'rounded-full shadow-xl',
            'px-6 py-3',
            'font-semibold',
            'flex items-center gap-2',
            'hover:bg-ctxt-blue',
            'transition transform hover:-translate-y-0.5',
        ].join(' '),
    }

    // Classes to use when disabled: greyed out with no hover effects,
    // and pointer events disabled.
    const disabledClasses =
        'bg-gray-300 text-gray-500 border border-gray-300 cursor-not-allowed opacity-50 pointer-events-none'

    const finalClassName = twMerge(
        baseClasses,
        sizeClasses,
        enabled ? enabledClasses[style] : disabledClasses,
        classes,
    )

    switch (props.type) {
        case 'link': {
            const { to, target, onClick } = props
            return (
                <div>
                    <Link
                        to={to}
                        className={finalClassName}
                        target={target}
                        onClick={onClick}
                    >
                        {children}
                    </Link>
                </div>
            )
        }
        case 'button':
            return (
                <div>
                    <button
                        className={finalClassName}
                        onClick={props.onClick}
                        disabled={!enabled}
                    >
                        {children}
                    </button>
                </div>
            )
        case 'submit':
            return (
                <div>
                    <button
                        className={finalClassName}
                        type="submit"
                        disabled={!enabled}
                    >
                        {children}
                    </button>
                </div>
            )
    }
}

export default Button
